import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Card, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, AlertCircle, Sparkles, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Routes } from '#Routes';
import { useFetchAttentionCenterQuery, normalizePreventionShieldApiLanguage } from '#store';

const ICON_SIZE = 20;

/** Icons per alert type; remaining layout in dashboard-layout-overrides.css (Volt/Bootstrap vs Tailwind). */
const ALERT_ICONS = {
  danger: AlertTriangle,
  warning: AlertCircle,
  ai_update: Sparkles,
  success: CheckCircle2,
};

function alertTypeKey(type) {
  const k = typeof type === 'string' ? type : '';
  return k in ALERT_ICONS ? k : 'warning';
}

/**
 * Attention center — GET /medapp/attention-center/
 */
export function AttentionCenterCard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const userToken = useSelector((s) => s.auth.userToken);

  const apiLanguage = useMemo(
    () => normalizePreventionShieldApiLanguage(i18n.language),
    [i18n.language],
  );

  const { data, isLoading, isError, isFetching, refetch } = useFetchAttentionCenterQuery(
    apiLanguage,
    { skip: !userToken },
  );

  const alerts = useMemo(() => (Array.isArray(data?.alerts) ? data.alerts : []), [data?.alerts]);

  const showLoading = isLoading && !data;

  return (
    <Card className='attention-center-card attention-center-card--shell h-100 w-100 shadow-sm'>
      <Card.Header className='bg-transparent border-bottom py-3 pb-2'>
        <div
          className='mb-0 d-flex align-items-center gap-2 fw-semibold text-dark'
          style={{ fontSize: '1rem' }}
        >
          <Bell
            className='flex-shrink-0'
            style={{ width: '1.25rem', height: '1.25rem', color: '#d97706' }}
            strokeWidth={2}
            aria-hidden
          />
          {t('dashboard.attention-center-title')}
          {isFetching && !isLoading ? (
            <Spinner animation='border' size='sm' className='ms-auto opacity-50' aria-hidden />
          ) : null}
        </div>
      </Card.Header>
      <Card.Body className='pt-3 pb-3 d-flex flex-column'>
        {isError ? (
          <div className='text-center'>
            <p className='mb-2 small text-muted'>{t('dashboard.attention-center-fetch-error')}</p>
            <Button variant='outline-secondary' size='sm' type='button' onClick={() => refetch()}>
              {t('dashboard.attention-center-retry')}
            </Button>
          </div>
        ) : null}

        {!isError && showLoading ? (
          <div
            className='d-flex flex-grow-1 align-items-center justify-content-center py-4'
            role='status'
            aria-live='polite'
            aria-label={t('dashboard.attention-center-loading-aria')}
          >
            <Spinner animation='border' size='sm' className='text-muted' />
          </div>
        ) : null}

        {!isError && !showLoading ? (
          <div className='attention-center-alerts'>
            {alerts.map((alert, index) => {
              const tkey = alertTypeKey(alert?.type);
              const Icon = ALERT_ICONS[tkey];
              const key = alert?.code != null ? String(alert.code) : `alert-${index}`;

              return (
                <div
                  key={key}
                  className={`attention-center-alert-item attention-center-alert-item--${tkey}`}
                >
                  <Icon
                    className='attention-center-alert-item__icon'
                    size={ICON_SIZE}
                    strokeWidth={2}
                    aria-hidden
                  />
                  <div className='attention-center-alert-item__content'>
                    {alert?.title ? (
                      <p className='attention-center-alert-item__title'>{alert.title}</p>
                    ) : null}
                    {alert?.type === 'ai_update' ? (
                      <p className='attention-center-alert-item__text'>
                        {alert?.message ?? ''}{' '}
                        <strong>
                          <Button
                            type='button'
                            variant='link'
                            className='attention-center-ai-cta p-0 align-baseline text-decoration-none fw-bold'
                            onClick={() => navigate(Routes.MedicalPersonaAi.path)}
                          >
                            [{t('dashboard.attention-update-persona-now')}]
                          </Button>
                        </strong>
                      </p>
                    ) : (
                      <p className='attention-center-alert-item__text'>{alert?.message ?? ''}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}
